import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box, HStack, Heading, Text, VStack, Button, ButtonText, Progress, ProgressFilledTrack, Icon, Center,
} from '@gluestack-ui/themed';
import {
  ArrowLeft, BrainCircuit, CheckCircle, Circle, AlertCircle,
} from 'lucide-react-native';
import { useNavigation } from '@react-navigation/native';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import log from '@/common/utils/logger';
import { Stage1ConceptCartography } from './Stage1ConceptCartography';
import { Stage2GraphSelection } from './Stage2GraphSelection';
import { Stage3ScopingQuestions } from './Stage3ScopingQuestions';
import { Stage4ModuleStrategy } from './Stage4ModuleStrategy';
import { ModuleDraftResult } from './ModuleDraftResult';
import { ContentStudioProvider, useContentStudioStore } from './store';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ContentStudioErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    log.error('ContentStudio Error:', error, errorInfo);
  }

  render() {
    const { hasError, error } = this.state;
    if (hasError) {
      return (
        <Center flex={1} bg="$bgApp" p="$6">
          <VStack space="md" alignItems="center">
            <Icon as={AlertCircle} size="xl" color="$error500" />
            <Heading size="lg">Something went wrong</Heading>
            <Text textAlign="center" color="$textSubtle">
              The Content Studio encountered an error. This usually happens during development when files are moved.
            </Text>
            <Box p="$4" bg="$bgSubtle" borderRadius="$md" w="100%">
              <Text size="xs" fontFamily="monospace" color="$error600">
                {error?.toString()}
              </Text>
            </Box>
            <Button mt="$4" onPress={() => window.location.reload()}>
              <ButtonText>Reload Application</ButtonText>
            </Button>
          </VStack>
        </Center>
      );
    }

    const { children } = this.props;
    return children;
  }
}

const FEATURE_FLAG = 'lecturer-content-studio';

const Steps = [
  { id: 1, title: 'Discovery', icon: BrainCircuit },
  { id: 2, title: 'Concept Selection', icon: Circle },
  { id: 3, title: 'Scope Refinement', icon: Circle },
  { id: 4, title: 'Module Strategy', icon: Circle },
  { id: 5, title: 'Draft Review', icon: CheckCircle },
];

const ContentStudioContent = () => {
  const navigation = useNavigation();
  const { currentStage } = useContentStudioStore();

  const renderStage = () => {
    switch (currentStage) {
      case 1:
        return <Stage1ConceptCartography />;
      case 2:
        return <Stage2GraphSelection />;
      case 3:
        return <Stage3ScopingQuestions />;
      case 4:
        return <Stage4ModuleStrategy />;
      case 5:
        return <ModuleDraftResult />;
      default:
        return <Stage1ConceptCartography />;
    }
  };

  const progress = (currentStage / Steps.length) * 100;

  return (
    <Box flex={1} bg="$bgApp">
      {/* Header */}
      <HStack px="$6" py="$4" borderBottomWidth={1} borderColor="$borderLight" alignItems="center" justifyContent="space-between">
        <HStack alignItems="center" space="md">
          <Button variant="link" onPress={() => navigation.goBack()} p="$2">
            <Icon as={ArrowLeft} size="xl" color="$textSubtle" />
          </Button>
          <VStack>
            <Heading size="md">Content Studio</Heading>
            <Text size="sm" color="$textSubtle">AI-Powered Module Designer</Text>
          </VStack>
        </HStack>
        <Box w={200}>
          <Progress value={progress} size="xs" bg="$bgSubtle">
            <ProgressFilledTrack bg="$brandBase" />
          </Progress>
          <Text size="xs" color="$textHint" textAlign="right" mt="$1">
            Step
            {' '}
            {currentStage}
            {' '}
            of
            {' '}
            {Steps.length}
          </Text>
        </Box>
      </HStack>

      {/* Main Content */}
      <Box flex={1}>
        {renderStage()}
      </Box>
    </Box>
  );
};

const ContentStudio = () => {
  const { isFeatureActive } = useUserFeatures();
  const navigation = useNavigation();

  if (!isFeatureActive(FEATURE_FLAG)) {
    return (
      <Center flex={1} bg="$bgApp">
        <Heading>Feature Locked</Heading>
        <Text>The Content Studio is currently in private beta.</Text>
        <Button mt="$4" onPress={() => navigation.goBack()}>
          <ButtonText>Go Back</ButtonText>
        </Button>
      </Center>
    );
  }

  return (
    <ContentStudioProvider>
      <ContentStudioErrorBoundary>
        <ContentStudioContent />
      </ContentStudioErrorBoundary>
    </ContentStudioProvider>
  );
};

export default ContentStudio;
